import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
    const nav = useNavigate();
    const loc = useLocation();
    const { login, user } = useAuth();
    const [form, setForm] = useState({ username: "admin", password: "" });
    const [loading, setLoading] = useState(false);

    if (user) nav("/admin", { replace: true });

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(form.username, form.password);
            toast.success("Welcome back");
            const to = loc.state?.from?.pathname || "/admin";
            nav(to, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dark min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
            {/* Editorial cover */}
            <div className="relative overflow-hidden hidden lg:block noise">
                <div className="absolute inset-0 bg-[radial-gradient(1000px_circle_at_20%_20%,hsl(var(--sea-glass)/0.28),transparent_55%),radial-gradient(700px_circle_at_80%_70%,hsl(var(--ocean)/0.22),transparent_60%)]" />
                <motion.svg
                    viewBox="0 0 500 500"
                    className="absolute inset-0 h-full w-full opacity-70"
                    initial={{ rotate: 0 }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 90, ease: "linear", repeat: Infinity }}
                >
                    {Array.from({ length: 12 }).map((_, i) => (
                        <ellipse key={i} cx="250" cy="250" rx={220 - i * 12} ry={110 + i * 5} fill="none" stroke="white" strokeOpacity="0.18" strokeWidth="1" transform={`rotate(${i * 15} 250 250)`} />
                    ))}
                </motion.svg>
                <div className="relative z-10 p-10 flex flex-col h-full">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] opacity-70">Aditya Baheti · Editorial Desk</p>
                    <div className="mt-auto">
                        <p className="font-display text-4xl leading-tight max-w-md">A quiet room to edit the site, publish scholarship, and manage the record.</p>
                        <p className="mt-4 text-sm opacity-70 max-w-md">Sign in to update content, manage publications, and refresh the profile photo.</p>
                    </div>
                </div>
            </div>

            {/* Login form */}
            <div className="grid place-items-center p-8">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
                    <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-paper/70 hover:text-paper" data-testid="admin-back-to-site-link">← Back to site</Link>
                    <Card className="mt-4 p-7 rounded-2xl bg-white/[0.04] border-white/10 backdrop-blur">
                        <div className="flex items-center gap-2 text-sm opacity-80">
                            <ShieldCheck className="h-4 w-4" /> Admin sign-in
                        </div>
                        <h1 className="mt-2 font-display text-3xl text-paper">Welcome back.</h1>
                        <p className="mt-1 text-sm opacity-70">Use your admin credentials to continue.</p>
                        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                            <div className="space-y-1.5">
                                <Label className="text-paper/80" htmlFor="u">Username</Label>
                                <Input id="u" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required autoComplete="username" className="bg-white/[0.06] border-white/10 text-paper" data-testid="admin-login-username-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-paper/80" htmlFor="p">Password</Label>
                                <Input id="p" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete="current-password" className="bg-white/[0.06] border-white/10 text-paper" data-testid="admin-login-password-input" />
                            </div>
                            <Button type="submit" disabled={loading} className="w-full bg-paper text-ink hover:bg-paper/90" data-testid="admin-login-submit-button">
                                {loading ? "Signing in…" : (<><LogIn className="h-4 w-4 mr-2" /> Sign in</>)}
                            </Button>
                        </form>
                        <p className="mt-4 text-xs opacity-60 font-mono">Default: admin / admin123 (change in settings)</p>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
}
